import { CaseSystemType } from '../../../../lib/api/self-serve/use-self-serve-api';
import {
  CaseApiModel,
  CaseFileApiModel,
  CaseRequirementApiModel,
  CaseSearchApiModel
} from '../../../../lib/api/self-serve/types';

interface ClaimAnalyser {
  willRefundCarriage: boolean;
  hasProtectionOffer: boolean;
  acceptedPrePayOffer: boolean;
  cashOffer: number;
  prePayOffer: number;
  status: string;
  resolutionReason: string;
  currencyCode: string;
}

export type RejectionReasonType =
  | ''
  | 'cover not taken'
  | 'parcel protection not taken'
  | 'contents prohibited'
  | 'no protection item'
  | 'no compensation'
  | 'packaging inadequate'
  | 'outside claim window'
  | 'no contact from customer'
  | 'no longer required'
  | 'incorrect weight/dims'
  | 'destroyed by customs'
  | 'left at authorised delivery point'
  | 'left unattended before collection'
  | 'multiple parcels strapped together'
  | 'parcel delivered'
  | 'covered for loss only'
  | 'protected for loss only';

export type ClaimOfferType = 'PREPAY' | 'CASH' | 'REJECTED' | 'UNKNOWN';

export function getOfferType(name: string): ClaimOfferType {
  if (!name) {
    return 'UNKNOWN';
  }
  if (name.indexOf('Accept PrePay Offer') > -1) {
    return 'PREPAY';
  }
  if (name.indexOf('Accept Cash Offer') > -1) {
    return 'CASH';
  }
  if (name.indexOf('Reject') > -1) {
    return 'REJECTED';
  }
  return 'UNKNOWN';
}

export function sanitiseClaimOfferDescriptions(desc: string): ClaimOfferType | string {
  switch (getOfferType(desc)) {
    case 'CASH':
      return 'Cash';
    case 'PREPAY':
      return desc.indexOf('Instant') > -1 ? 'PrePay (Instant)' : 'PrePay';
    case 'REJECTED':
      return 'Reject';
    default:
      return desc;
  }
}

export function getRef(claim: CaseApiModel) {
  return `${claim.metaData.find((x) => x.name == 'Ref')?.value ?? claim.internalId} / ${claim.id}`;
}

export function getCurrency(claim: CaseApiModel) {
  const currency = claim.metaData.find((x) => x.name === 'Order Currency')?.value;
  if (!currency || currency.includes('*')) {
    return null;
  }
  return currency;
}

export function getDeliveryAddress(claim: CaseApiModel) {
  return claim.metaData.find((x) => x.name === 'Delivery Address')?.value;
}

export function getDeliveryEmail(claim: CaseApiModel) {
  return claim.metaData.find((x) => x.name === 'Customer Email')?.value;
}

export function getDeliveryContactName(claim: CaseApiModel) {
  return claim.metaData.find((x) => x.name === 'Delivery Contact Name')?.value;
}

export function getSystemDescription(system: CaseSystemType) {
  switch (system) {
    case 'CLAIMS':
      return 'claim';
    case 'SUPPORT':
      return 'support ticket';
  }
}

export function getParcelContents(claim: CaseApiModel) {
  return claim.metaData.find((x) => x.name === 'Parcel Contents')?.value;
}

export function getDocumentRejectionReason(claim: CaseApiModel) {
  return claim.metaData.find((x) => x.name === 'Documents Reject Reason')?.value;
}

export function requirementIsRequired(requirement: CaseRequirementApiModel): boolean {
  return !!requirement.validators.find((x) => x.validatorType === 'Required');
}

export function requirementIsFile(requirement: CaseRequirementApiModel): boolean {
  return requirement.requirementType === 'file';
}

export function requirementCanBeOverridden(requirement: CaseRequirementApiModel): boolean {
  return requirementIsFile(requirement) && requirement.name !== 'Proof Of Value';
}

export function requirementHasValue(requirement: CaseRequirementApiModel): boolean {
  return requirement.value !== null && requirement.value !== '' && requirement.value !== undefined;
}

export function fileIsDisposed(requirement: CaseRequirementApiModel) {
  return requirementIsFile(requirement) && requirement.value?.toLowerCase().indexOf('disposed') > -1;
}

export function requirementIsInvalid(requirement: CaseRequirementApiModel): boolean {
  if (requirementIsFile(requirement) && fileIsDisposed(requirement)) {
    return false;
  }
  return requirementIsRequired(requirement) && !requirementHasValue(requirement);
}

export function getResolutionReason(claim: CaseApiModel): string {
  if (claim.caseStatus.name === 'Resolved') {
    const desc = claim.metaData.find((m) => m.name === 'Resolve Reason');
    const reason = claim.metaData.find((m) => m.name === 'Resolved Additional Information');
    if (desc && reason) {
      return `${desc.value}. ${reason.value}`;
    }
    if (desc) {
      return desc.value;
    }
  }

  if (claim.caseStatus.name === 'Rejected') {
    const desc = claim.metaData.find((m) => m.name === 'Rejection Reason');
    const reason = claim.metaData.find((m) => m.name === 'Rejection Additional Information');
    if (desc && reason) {
      return `${desc.value}. ${reason.value}`;
    }
    if (desc) {
      return desc.value;
    }
  }

  return claim.caseStatus.name;
}

export function getClaimMoney(claim: CaseApiModel, keys: string[]): number {
  let output = 0;
  keys.forEach((key) => {
    const data = claim.metaData.find((m) => m.name === key)?.value;
    if (data) {
      output += parseFloat(data);
    }
  });
  return output;
}

export type CaseStatusType = 'Courier' | 'Action' | 'Review' | 'Outcome' | '';

export type CaseStatusName =
  | 'Pending Courier'
  | 'Awaiting POD'
  | 'New'
  | 'Pending Documents'
  | 'Pending Review'
  | 'Approved'
  | 'Offer Rejected'
  | 'Offer Made'
  | 'Pending BACs Approval'
  | 'Pending Investigation'
  | 'Rejected'
  | 'Resolved'
  | 'Paid'
  | 'Closed'
  | 'Resolved'
  | 'Pending Customer'
  | 'Pending Customer Warned';

export function getClaimStatusFromSnapshot(claim: CaseSearchApiModel): CaseStatusType {
  return getCaseStatus({ caseStatus: claim.status } as CaseApiModel);
}

export function getCaseStatus(claim: CaseApiModel): CaseStatusType {
  switch (claim.caseStatus?.name as CaseStatusName) {
    case 'Pending Courier':
    case 'Awaiting POD':
      return 'Courier';
    case 'Pending Customer':
    case 'Pending Customer Warned':
    case 'Pending Documents':
    case 'Offer Made':
      return 'Action';
    case 'Pending Review':
    case 'Approved':
    case 'Offer Rejected':
    case 'Pending BACs Approval':
      return 'Review';
    case 'Rejected':
    case 'Resolved':
    case 'Closed':
    case 'Paid':
      return 'Outcome';
    default:
      return '';
  }
}

export function getCaseStatusByStatusName(name: string): CaseStatusType {
  return getCaseStatus({ caseStatus: { name: name } } as CaseApiModel);
}

export function getCashOffer(claim: CaseApiModel): number {
  return getClaimMoney(claim, ['Cash Amount']);
}

export function getPrePayOffer(claim: CaseApiModel): number {
  return getClaimMoney(claim, ['PrePay Amount', 'PrePay Goodwill Amount']);
}

export function customerAcceptedPrePayOffer(claim: CaseApiModel): boolean {
  return claim.metaData.find((x) => x.name === 'PrePay Offer')?.value === 'True';
}

export function parcelWasCovered(claim: CaseApiModel): boolean {
  return claim.metaData.find((x) => x.name === 'Covered')?.value === 'True';
}

export function parcelCoveredAmount(claim: CaseApiModel): number {
  return parcelWasCovered(claim)
    ? parseFloat(claim.metaData.find((x) => x.name === 'Covered Amount')?.value)
    : undefined;
}

export function carriageWillBeRefunded(claim: CaseApiModel): boolean {
  return claim.metaData.find((x) => x.name === 'Refund Carriage')?.value === 'True';
}

export function getResolvedFiles(claim: CaseApiModel): CaseFileApiModel[] {
  const files = claim.metaData.find((x) => x.name === 'Resolved Files')?.value;
  return files ? JSON.parse(files) : [];
}

export function parseRequirementFiles(requirement: CaseRequirementApiModel): CaseFileApiModel[] {
  try {
    return requirement.value ? (JSON.parse(requirement.value) as CaseFileApiModel[]) : [];
  } catch {
    return [];
  }
}

export function isAccepedFile(fileName: string) {
  return isAccepedImageFileName(fileName) || isAccepedDocumentFileName(fileName);
}

export const acceptableImageFormats = ['.png', '.jpg', '.jpeg', '.bmp', '.gif', '.tiff'];
export const acceptableFileFormats = ['.pdf', '.doc', '.docx', '.mp4'];

// Case files are posted through the edge, and API Gateway base64-encodes multipart bodies before
// its 6,291,456 byte limit applies, so a file over about 4.5 MB (6,291,456 x 3/4) is rejected before
// it reaches the edge. 4 MB encodes to 5,592,408 bytes, leaving about 680 KB for the multipart
// envelope and the request headers.
export const maxCaseFileSizeMb = 4;
export const maxCaseFileSizeBytes = maxCaseFileSizeMb * 1024 * 1024;

export function isCaseFileTooLarge(file: File): boolean {
  return file.size > maxCaseFileSizeBytes;
}

export function isAccepedImageFileName(fileName: string): boolean {
  return !!acceptableImageFormats.find((x) => fileName?.toLowerCase().indexOf(x) > 0);
}

export function isAccepedDocumentFileName(fileName: string): boolean {
  return !!acceptableFileFormats.find((x) => fileName?.toLowerCase().indexOf(x) > 0);
}

export function acceptableFileFormatString() {
  const input = [...acceptableImageFormats, ...acceptableFileFormats];
  const last = input.pop();
  return (input.join(', ') + ' and ' + last).replaceAll('.', '');
}

export function AnalyiseCase(claim: CaseApiModel): ClaimAnalyser {
  const acceptedPrePayOffer = customerAcceptedPrePayOffer(claim);
  const willRefundCarriage = carriageWillBeRefunded(claim);
  const cashOffer = getCashOffer(claim);
  const prePayOffer = getPrePayOffer(claim);
  const status = getCaseStatus(claim);
  const resolutionReason = getResolutionReason(claim);
  const currencyCode = getCurrency(claim);

  return {
    willRefundCarriage,
    prePayOffer,
    cashOffer,
    hasProtectionOffer: cashOffer !== 0 || prePayOffer !== 0,
    acceptedPrePayOffer,
    status,
    resolutionReason,
    currencyCode
  };
}
